import * as React from "react"
import { Row, Col, Container } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../../atoms/spacers"
import TextButton from "../../../atoms/buttons/TextButton"
import styled from "styled-components"
import { theme } from "../../../theme"
import H3 from "../../../atoms/typography/H3"
import H2 from "../../../atoms/typography/H2"
import { Link } from "react-router-dom"
import Body from "../../../atoms/typography/Body"
import LocalOfferIcon from "../../../atoms/icons/LocalOfferIcon"
import Counter from "../../../molecules/Counter"
import { ArrowRight } from "../../../atoms/icons/ArrowRight"
import DatePicker from "../../../atoms/inputs/DatePicker"
import SmallColorfulButton from "../../../atoms/buttons/SmallColorfulButton"
import Card from "../../../atoms/Card"
import { ShareIcon } from "../../../atoms/icons/ShareIcon"
import { ArrowDropUpIcon } from "../../../atoms/icons/ArrowDropUpIcon"
import { ArrowDropDownIcon } from "../../../atoms/icons/ArrowDropDownIcon"
import { SlideOutHeight } from "../../../atoms/animations/SlideOutHeight"
import SelectDataPlanButton from "../../../molecules/store/CustomizeAndOrder/SelectDataPlanButton"
import { DownloadIcon } from "../../../atoms/icons/DownloadIcon"
import Line from "../../../atoms/Line"
import { useRentalDataPlansQuery } from "../../../../hooks-saleor/useRentalDataPlansQuery"
import { useRentalTerminalQuery } from "../../../../hooks-saleor/useRentalTerminalQuery"
import {
  PRODUCT_RENTAL_DATA_PLAN_HALF_YEAR_SLUG,
  PRODUCT_RENTAL_DATA_PLAN_QUARTERLY_SLUG,
} from "../../../../utils/saleor-constants"
import { getFormattedPrice } from "../../../../utils/cart-helpers"
import { CartStructure, RentalDataPlanVariant, Product } from "../../../interfaces/SaleorTypes"
import DropDown, { Option, optionsMap } from "../../../atoms/inputs/Dropdown"
import RentalPriceBreakdown from "../../../molecules/store/RentalMolecules/RentalPriceBreakdown"
import { getDateIn3BusinessDays } from "../../../../utils/helpers"
import { Key } from "react-aria-components"

const PaddedCard = styled(Card)`
  padding: 32px;
`

const NumberContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.greyDark};
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`

const OPTIONS_MONTHS: Option<number>[] = Array.from({ length: 6 }, (_, i) => ({
  id: i,
  value: i + 1,
  name: `${i + 1} month${i + 1 > 1 ? "s" : ""}`,
}))

interface Props {
  setShowRentalCheckout: (value: boolean) => void
  setRentalOrder: (value: CartStructure[]) => void
  selectedDate: string
  setSelectedDate: (value: string) => void
}

const CustomizeRentalAndOrder: React.FC<Props> = ({
  setShowRentalCheckout,
  setRentalOrder,
  selectedDate,
  setSelectedDate,
}) => {
  const [amount, setAmount] = React.useState(1)
  const [selectedMonthsId, setSelectedMonthsId] = React.useState<Key>(0)
  const [showProductInfo, setShowProductInfo] = React.useState(false)
  const [selectedDataIndex, setSelectedDataIndex] = React.useState<number>(0)
  const [periodCost, setPeriodCost] = React.useState(0)
  const [startCost, setStartCost] = React.useState(0)
  const [totalCost, setTotalCost] = React.useState(0)
  const [subTotalCost, setSubTotalCost] = React.useState(0)
  const [dataPlans, setDataPlans] = React.useState<RentalDataPlanVariant[]>()

  const selectedMonths = optionsMap(OPTIONS_MONTHS)[selectedMonthsId].value!

  const { dataPlanProduct, dataPlans: dataPlansQuarterly } = useRentalDataPlansQuery(
    PRODUCT_RENTAL_DATA_PLAN_QUARTERLY_SLUG,
  )
  const { dataPlans: dataPlansHalfYear } = useRentalDataPlansQuery(PRODUCT_RENTAL_DATA_PLAN_HALF_YEAR_SLUG)
  const { rentalTerminal, initialCostProduct, monthlyCostProduct } = useRentalTerminalQuery()

  const selectedData = () => {
    return dataPlans ? dataPlans[selectedDataIndex] : undefined
  }
  const selectedDataCost = () => {
    return selectedData()?.pricing?.price?.gross?.amount || 0
  }
  const tryGoToCheckout = () => {
    if (
      amount > 0 &&
      selectedMonths > 0 &&
      selectedData() &&
      initialCostProduct &&
      monthlyCostProduct &&
      rentalTerminal &&
      dataPlanProduct
    ) {
      let initCostSaleorProduct: Product = {
        ...rentalTerminal,
        variants: [initialCostProduct],
      }
      let monthlyCostSaleorProduct: Product = {
        ...rentalTerminal,
        variants: [monthlyCostProduct],
      }
      let dataSaleorProduct: Product = {
        ...dataPlanProduct,
        variants: [selectedData()!],
      }
      setRentalOrder([
        {
          product: initCostSaleorProduct,
          amount: amount,
        },
        {
          product: monthlyCostSaleorProduct,
          amount: amount * selectedMonths,
        },
        {
          product: dataSaleorProduct,
          amount: amount,
        },
      ])
    }
    setShowRentalCheckout(true)
  }

  React.useEffect(() => {
    setSelectedDataIndex(selectedDataIndex)
    if (selectedMonths <= 3) {
      setDataPlans(dataPlansQuarterly)
    } else {
      setDataPlans(dataPlansHalfYear)
    }
    setPeriodCost((monthlyCostProduct?.pricing?.price?.gross?.amount || 0) * selectedMonths)
  }, [selectedMonths, selectedDataIndex, amount, dataPlansQuarterly, dataPlansHalfYear])

  React.useEffect(() => {
    if (periodCost === 0 && monthlyCostProduct) {
      setPeriodCost((monthlyCostProduct.pricing?.price?.gross?.amount || 0) * selectedMonths * amount)
    }
    if (startCost === 0 && initialCostProduct) {
      setStartCost((initialCostProduct.pricing?.price?.gross?.amount || 0) * amount)
    }
  }, [initialCostProduct])

  React.useEffect(() => {
    setTotalCost((periodCost + startCost + selectedDataCost()) * amount)
    setSubTotalCost(periodCost + startCost + selectedDataCost())
  }, [periodCost, startCost, selectedDataIndex, amount, dataPlans])

  return (
    <Container>
      <PaddedCard>
        <Row nogutter align="center">
          <NumberContainer>
            <LocalOfferIcon />
          </NumberContainer>
          <Spacer spacing={1} />
          <H3>Book rental</H3>
        </Row>
        <HorizontalSpacer spacing={2} />
        <Row>
          <Col lg={1} />
          <Col lg={5} style={{ paddingRight: 32 }}>
            <Row justify="between" nogutter>
              <H3>Number of terminals</H3>
              <Counter
                minValue={1}
                onClickAdd={() => setAmount(amount + 1)}
                onClickMinus={() => setAmount(amount - 1)}
                currentValue={amount}
              />
            </Row>
            <HorizontalSpacer spacing={0.5} />
            <Body>
              Amount of Terminals.{" "}
              {`${getFormattedPrice(
                initialCostProduct?.pricing.price.gross.amount ?? 0,
                initialCostProduct?.pricing.price.currency,
              )} initial fee.`}
            </Body>
            <HorizontalSpacer spacing={1.5} />
            <Row gutterWidth={1.5}>
              <Col xs={12} xl={6}>
                <H3>Desired start date:</H3>
                <HorizontalSpacer spacing={1.4} />
                <DatePicker
                  value={selectedDate.toString()}
                  onSelectDate={(date) => setSelectedDate(date)}
                  minDate={getDateIn3BusinessDays()}
                  useMinDate={true}
                />
                <HorizontalSpacer spacing={1} />
              </Col>
              <Col xs={12} xl={6}>
                <H3>Number of months:</H3>
                <HorizontalSpacer spacing={1.4} />
                <DropDown
                  label="Number of months"
                  options={OPTIONS_MONTHS}
                  onSelectionChange={(id) => setSelectedMonthsId(id)}
                  selectedKey={selectedMonthsId}
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={1.5} />
            <H3>Amount of data: *</H3>
            <HorizontalSpacer spacing={0.5} />
            <GridContainer>
              {dataPlans &&
                dataPlans.map((dataPlan: RentalDataPlanVariant, index: number) => (
                  <SelectDataPlanButton
                    key={dataPlan.id}
                    title={dataPlan.name}
                    price={getFormattedPrice(dataPlan.pricing.price.gross.amount, dataPlan.pricing.price.currency)}
                    onClick={() => setSelectedDataIndex(index)}
                    selected={selectedData()?.dataPackageGB === dataPlan.dataPackageGB}
                    gradient="#EABB90"
                    small
                  />
                ))}
            </GridContainer>
            <HorizontalSpacer spacing={1.5} />
            <Link to="/rental-terms" target="_blank">
              <TextButton title="Terms and conditions" icon={<ShareIcon />} />
            </Link>
            <HorizontalSpacer spacing={1} />
            <TextButton
              title="Details"
              icon={showProductInfo ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              onClick={() => setShowProductInfo(!showProductInfo)}
            />
            <SlideOutHeight isVisible={showProductInfo}>
              <HorizontalSpacer spacing={0.5} />
              <Body>
                For more information about the pricing and data packages see our service specification. The rental
                period can be extended, and more data can be purchased on our website. Each started month is billed as a
                whole month *Data packages are purchased per terminal. Contact us if you want to share a package between
                terminals.
              </Body>
              <HorizontalSpacer spacing={0.5} />
              <TextButton title="Download service specification" icon={<DownloadIcon />} />
            </SlideOutHeight>
          </Col>
          <Col
            lg={5}
            style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", paddingLeft: 32 }}
          >
            <div>
              <H3>Satcube Instant Package</H3>
              <HorizontalSpacer spacing={0.25} />
              <Line />
              {initialCostProduct && monthlyCostProduct && dataPlans && dataPlans[selectedDataIndex] && (
                <RentalPriceBreakdown
                  rentalTerminalInitialFeePrice={initialCostProduct.pricing.price.gross.amount}
                  rentalMonthlyFeePrice={monthlyCostProduct.pricing.price.gross.amount}
                  numberOfMonths={selectedMonths}
                  amountOfRentals={amount}
                  chosenDataPackage={dataPlans[selectedDataIndex]}
                  chosenDataPackagePrice={selectedDataCost()}
                  currency={initialCostProduct?.pricing.price.currency}
                  selectedDate={selectedDate}
                  subTotal={subTotalCost}
                />
              )}
              <HorizontalSpacer spacing={1.5} />
              <H2 textAlign="center">{`Total: 
              ${getFormattedPrice(totalCost, monthlyCostProduct?.pricing.price.currency)}`}</H2>
              <HorizontalSpacer spacing={1.5} />
            </div>

            <Row nogutter justify="end">
              <SmallColorfulButton
                variant="yellow"
                title="Shipping & checkout"
                iconRight={<ArrowRight color={theme.colors.greyDark} />}
                onClick={() => tryGoToCheckout()}
              />
            </Row>
          </Col>
          <Col lg={1} />
        </Row>
      </PaddedCard>
    </Container>
  )
}

export default CustomizeRentalAndOrder
